/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react"
import { TreeViewDataItem } from "store/sidebar/types"
import { FILTER_TYPES } from "helpers/constants/filters"
import { FilterCheckbox } from "./Checkbox"
import { FilterDropDown } from "./DropDown"
import { FilterButton } from "./Button"
import { FilterInput } from "./Input"
import { FilterTitle } from "./Title"
import { FilterLine } from "./Line"
import { ItemRenderProps } from "@progress/kendo-react-treeview"
import { ITreeElementProps } from "helpers/types/tree"
import Filter from "components/Filter/Filter"
import { Button } from "@progress/kendo-react-buttons"

import { MapOptions } from "store/workspace/types"
import { ROUTES } from "helpers/constants/app"
import { IPointModified } from 'api/mapView/types';
import "./TreeItem.scss"
import { IElementType } from "components/SideBarItems/types"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useLocation } from "react-router-dom"
import axios, { CancelTokenSource } from "axios"
import { workspaceSlice } from "store/workspace/slice"
import { saveMapOptionsThunk } from "store/workspace/thunk"
import { getMapViewFilteredPointsThunk } from "store/mapView/thunk"
import { eyeIcon, arrowDownIcon, arrowUpIcon } from "@progress/kendo-svg-icons" 
import StyleSettings from '../InfoWindow/styleSettings';
import ErrorPopup from "./ErrorPopup"
import LicenseErrorPopup from "./LicenseErrorPopup"
import useLicenseAccess from '../../hooks/useLicenseAccess';
import { selectActiveUser } from 'store/userState/selectors';
import { isReadOnlyAccess } from 'helpers/functions/workspaceHelpers';

export interface IInfoWindowContent {
    attributesValues: IAttributesValues[];
    dataset?: string
    report: string | null;
    layer: string;
    label: string;
    existsInMap: boolean;
    object?: IPointModified;
    existsProjectView?: boolean
}

export interface IAttributesValues {
    text: string;
    id: string,
    isChanged: boolean,
    name: string,
    type: number,
    value: string,
    x: number,
    y: number
}

export interface ITempDataSideBar {
  identifierFieldId: string,
  identifierFieldType: string,
  isUserData: boolean,
  label: string
  latitude: number,
  longitude: number,
  mapViewPointOption: any,
  outline: [],
  radius: number,
  tooltips: []
}

const TreeItem = ({ ...props }: ItemRenderProps & { item: TreeViewDataItem }) => {
  const dispatch = useAppDispatch()
  
  // Memoized selectors to prevent unnecessary re-renders
  const { tabs, mapOptions, workspaceId, tree, workspaces } = useAppSelector((state) => state.workspaceSlice)
  const { mapViewData } = useAppSelector((state) => state.mapViewSlice)
  const user = useAppSelector(selectActiveUser)
  
  // License access control
  const { checkDatasetAccess, blockUnauthorizedAction } = useLicenseAccess()
  
  // Check if current workspace is read-only
  const currentWorkspace = useMemo(() => workspaces.find(ws => ws.id === workspaceId) || null, [workspaces, workspaceId])
  const isWorkspaceReadOnly = useMemo(() => isReadOnlyAccess(currentWorkspace, user?.id), [currentWorkspace, user?.id])
  
  // Helper function to find parent dataset ID from tree structure
  const findParentDatasetId = useCallback((itemId: string): string | null => {
    const findInTree = (items: TreeViewDataItem[], targetId: string, parentDatasetId: string | null = null): string | null => {
      for (const item of items) {
        // Check if this is a dataset item (has numeric ID 0-6)
        const isDatasetItem = /^[0-6]$/.test(item.id || '');
        const currentParentDatasetId = isDatasetItem ? item.id : parentDatasetId;
        
        if (item.id === targetId) {
          return currentParentDatasetId;
        }
        
        if (item.items && item.items.length > 0) {
          const result = findInTree(item.items, targetId, currentParentDatasetId);
          if (result !== null) return result;
        }
      }
      return null;
    };
    
    return findInTree(tree || [], itemId);
  }, [tree]);

  // Check if parent dataset has access
  const parentDatasetId = useMemo(() => findParentDatasetId(props.item.id || ''), [findParentDatasetId, props.item.id]);
  const parentDatasetAccess = useMemo(() => 
    parentDatasetId ? checkDatasetAccess(parentDatasetId) : { hasAccess: true, canVisualize: true, canFilter: true }, 
    [checkDatasetAccess, parentDatasetId]
  );
  
  // Check dataset access using comprehensive license system
  const datasetAccess = useMemo(() => 
    checkDatasetAccess(props.item.id), [checkDatasetAccess, props.item.id])

  // Determine if item should be visually disabled but visible
  // Include parent dataset access and workspace read-only status in the check
  const shouldDisableItem = useMemo(() => {
    return isWorkspaceReadOnly || !datasetAccess.hasAccess || !datasetAccess.canVisualize || !datasetAccess.canFilter ||
           !parentDatasetAccess.hasAccess || !parentDatasetAccess.canVisualize || !parentDatasetAccess.canFilter
  }, [isWorkspaceReadOnly, datasetAccess.hasAccess, datasetAccess.canVisualize, datasetAccess.canFilter,
      parentDatasetAccess.hasAccess, parentDatasetAccess.canVisualize, parentDatasetAccess.canFilter])

  // Helper function to show license error
  const showLicenseError = (action: string) => {
    // Check if it's a parent access issue or current item access issue
    let datasetName = props.item.name || 'this dataset';
    let reason = 'Your current license does not include access to this dataset';
    
    if (!parentDatasetAccess.hasAccess && parentDatasetId) {
      datasetName = `parent dataset (ID: ${parentDatasetId})`;
      reason = 'Your current license does not include access to the parent dataset';
    }
    
    setLicenseError({
      isOpen: true,
      action,
      datasetName,
      reason
    })
  }
  
  // Memoized derived values
  const selectedTab = useMemo(() => tabs?.find((item) => item.isSelected), [tabs])
  
  const [showStyles, setShowStyles] = useState(false)
  const [pointData, setPointData] = useState<any>()
  const [openErrorPopup, setOpenErrorPopup] = useState(false)
  const [licenseError, setLicenseError] = useState<{
    isOpen: boolean;
    action: string;
    datasetName: string;
    reason: string;
  }>({
    isOpen: false,
    action: '',
    datasetName: '',
    reason: ''
  })
  
  const location = useLocation()

  // Memoized function to check if filter is applied
  const isFilterApplied = useMemo(() => {
    const dropdownNames: (keyof typeof FILTER_TYPES)[] = [FILTER_TYPES.title, FILTER_TYPES.subMenu]
    if (!dropdownNames.includes(props.item.type as keyof typeof FILTER_TYPES)) return false
    if (!props.item.items?.length) return false

    return props.item.items.some((item: TreeViewDataItem) => {
      if (item.type === "subMenu") return props.item.items.some((subItem: TreeViewDataItem) => subItem.filterApplied)
      return false
    })
  }, [props.item.items, props.item.type])
  const mapEndpointSource = React.useRef<CancelTokenSource | null>(null)
  const isSaving = React.useRef(false)

  const handleSaveValues = useCallback(async (_?: IElementType, group?: string | null, updatedMapOptions?: any) => {
    if (isSaving.current) return; // Prevent concurrent executions
    if(workspaceId !== ""){
      isSaving.current = true;
      try {
        const optionsToSave = updatedMapOptions || mapOptions;
        await dispatch(saveMapOptionsThunk({ id: workspaceId, mapOptions: optionsToSave }))
        if (group === "serverSideOptions") {
          if (mapEndpointSource.current) mapEndpointSource.current.cancel()
          mapEndpointSource.current = axios.CancelToken.source()
          await dispatch(getMapViewFilteredPointsThunk({ workspaceId, source: mapEndpointSource.current }))
        }
      } finally {
        isSaving.current = false;
      }
    }
  }, [dispatch, workspaceId, mapOptions])

  const handleChangeValue = useCallback((obj: { element: IElementType; value: boolean; group: string | null; index?: number }): void => {
      dispatch(workspaceSlice.actions.setMapOptionItem(obj))

      const updatedMapOptions = {
        ...mapOptions,
        [obj.element]: obj.value
      };
      
      handleSaveValues(obj.element, obj.group, updatedMapOptions)
  }, [dispatch, handleSaveValues, mapOptions])
  const filterClassName = useMemo(() => {
    return isFilterApplied ? "text-primary" : "text-secondary"
  }, [isFilterApplied])

  const COMPONENTS_BY_FILTER_TYPE: { [key in keyof typeof FILTER_TYPES]: React.ComponentType<ITreeElementProps> } = {
    [FILTER_TYPES.title]: FilterTitle,
    [FILTER_TYPES.subMenu]: FilterTitle,
    [FILTER_TYPES.subTitle]: FilterTitle,
    [FILTER_TYPES.select]: FilterDropDown,
    [FILTER_TYPES.input]: FilterInput,
    [FILTER_TYPES.button]: FilterButton,
    [FILTER_TYPES.filter]: Filter,
    [FILTER_TYPES.numeric]: Filter,
    [FILTER_TYPES.checkbox]: FilterCheckbox,
    [FILTER_TYPES.line]: FilterLine,
  }

  const Component = COMPONENTS_BY_FILTER_TYPE[props.item.type as keyof typeof FILTER_TYPES] || FilterTitle;

  // Memoized values for performance
  const renderMapButtons = useMemo(() => {
    if (location.pathname.includes(ROUTES.explore)) {
      return selectedTab?.type === "mapView"
    }
    return location.pathname.includes(ROUTES.dashboard) || location.pathname.includes(ROUTES.booking)
  }, [selectedTab?.type, location.pathname])

  const buttonIdToElementMap = useMemo(() => ({
    "0": "subsurfaceAssets" as keyof MapOptions,
    "1": "globalPipelineNetworks" as keyof MapOptions,
    "2": "cO2Emitters" as keyof MapOptions,
    "3": "ccsProjects" as keyof MapOptions,
    "4": "blocks" as keyof MapOptions,
    "5": "licences" as keyof MapOptions,
    "6": "windfarms" as keyof MapOptions,
  }), [])

  const handleClick = async (item: any) => {
      try {
        const dataMap: Record<string, keyof typeof mapViewData> = {
          subsurfaceAssets: "storages",
          globalPipelineNetworks: "transports",
          cO2Emitters: "sources",
          blocks: "blocks",
          licences: "licences"

        };
        const key = dataMap[item] || (item as keyof typeof mapViewData);
        setPointData(mapViewData[key]?.[0] || null);
        setShowStyles(true);
      } catch (e) {
          console.error(e);
      };
  };

  const findBackgroundColor = (): string => {
    
    const itemId = props.item.id as string;
    const elementKey = buttonIdToElementMap[itemId as keyof typeof buttonIdToElementMap];
    if (!elementKey) return "rgba(200, 200, 200, 1)"; // Early return if invalid key
    
    const lookupMap: Record<string, string> = {
      subsurfaceAssets: "storages",
      globalPipelineNetworks: "transports",
      cO2Emitters: "sources",
      blocks: "blocks",
      licences: "licences"
    };
    
    const lookupKey = lookupMap[elementKey] || elementKey;
    const data : ITempDataSideBar | any = mapViewData[lookupKey as keyof typeof mapViewData]?.[0] || null;
    
    if (!data || !data?.mapViewPointOption?.color) {
      return "rgba(200, 200, 200, 1)"; // Fallback color (gray)
    }
    const [r, g, b] = data?.mapViewPointOption?.color || [255, 255, 10];

    return `rgba(${r}, ${g}, ${b}, 1)`;
  };

  const handleOrderChange = (direction: 'up' | 'down') => {
    const currentTree = JSON.parse(JSON.stringify(tree));
    
    const findItemAndParent = (
      items: TreeViewDataItem[], 
      itemId: string
    ): { items: TreeViewDataItem[], index: number } | null => {
      const index = items.findIndex(item => item.id === itemId);
      if (index !== -1) {
        return { items, index };
      }
      
      for (const item of items) {
        if (item.items && item.items.length) {
          const result = findItemAndParent(item.items, itemId);
          if (result) {
            return result;
          }
        }
      }
      
      return null;
    };
    
    const result = findItemAndParent(currentTree, props.item.id);
    
    if (!result) return;
    
    const { items, index } = result;
    
    let newIndex;
    if (direction === 'up') {
      newIndex = Math.max(0, index - 1);
    } else {
      newIndex = Math.min(items.length - 1, index + 1);
    }
    
    if (newIndex === index) return;
    
    const reorderedItems = [...items];
    const [removed] = reorderedItems.splice(index, 1);
    reorderedItems.splice(newIndex, 0, removed);
    
    for (let i = 0; i < reorderedItems.length; i++) {
      items[i] = reorderedItems[i];
    }
    
    dispatch(workspaceSlice.actions.setTree(currentTree));
    
    const itemId = props.item.id as string;
    const elementKey = buttonIdToElementMap[itemId as keyof typeof buttonIdToElementMap];
    if (elementKey) {
      // For order changes, we need to trigger map refresh with current mapOptions
      handleSaveValues(undefined, "serverSideOptions", mapOptions);
    }
  };



  // Always render items but disable them if needed (instead of hiding completely)

  return (
    <>
      {props.item.type === FILTER_TYPES.title || props.item.type === FILTER_TYPES.subMenu || props.item.type === FILTER_TYPES.subTitle ? (
        <div
          onClick={() => {
            // Block filter interactions for unauthorized datasets
            if (shouldDisableItem && !blockUnauthorizedAction(props.item.id, 'filter')) {
              showLicenseError('filter');
              return;
            }
            props.item.handleExpandChange?.(props.item)
          }}
          className={`${filterClassName} ${shouldDisableItem ? 'disabled-item' : ''}`}
          style={{ 
            cursor: shouldDisableItem ? "not-allowed" : "pointer", 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            opacity: shouldDisableItem ? 0.5 : 1
          }}
        >
          <Component key={props.item.id} props={props} appliedFilterClassName={filterClassName} disabled={shouldDisableItem} />
          
          {(renderMapButtons && buttonIdToElementMap[props.item.id as keyof typeof buttonIdToElementMap]) ? (
            <>
              <div 
                className="color-block" 
                style={{
                  background: findBackgroundColor(), 
                  border:'1px solid grey',
                  opacity: shouldDisableItem ? 0.5 : 1
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (shouldDisableItem || !blockUnauthorizedAction(props.item.id, 'style configuration')) {
                    showLicenseError('style configuration');
                    return;
                  }
                  const itemId = props.item.id as string;
                  handleClick(buttonIdToElementMap[itemId as keyof typeof buttonIdToElementMap])
              }}></div>
              { (props.item.type === FILTER_TYPES.title) ? <></> : 
                <Button 
                  className="no-margin-button margin-right-1"
                  svgIcon={arrowDownIcon} 
                  themeColor={"primary"} 
                  style={{ opacity: shouldDisableItem ? 0.5 : 1 }}
                  disabled={shouldDisableItem}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!blockUnauthorizedAction(props.item.id, 'reorder')) {
                      showLicenseError('reorder');
                      return;
                    }
                    handleOrderChange('down');
                  }} 
                />}
              { (props.item.type === FILTER_TYPES.title) ? <></> : 
                <Button 
                  className="no-margin-button margin-right-1"
                  svgIcon={arrowUpIcon} 
                  themeColor={"primary"} 
                  style={{ opacity: shouldDisableItem ? 0.5 : 1 }}
                  disabled={shouldDisableItem}
                  onClick={(event) => {
                    event.stopPropagation(); 
                    if (!blockUnauthorizedAction(props.item.id, 'reorder')) {
                      showLicenseError('reorder');
                      return;
                    }
                    handleOrderChange('up');
                  }}
                />}
              <Button
                className="no-margin-button"
                svgIcon={eyeIcon}
                style={{ marginLeft: "auto" }}
                disabled={shouldDisableItem}
                themeColor={
                    props.item.id === "0" && mapOptions.subsurfaceAssets
                    ? "primary"
                    : props.item.id === "1" && mapOptions.globalPipelineNetworks
                    ? "primary"
                    : props.item.id === "2" && mapOptions.cO2Emitters
                    ? "primary"
                    : props.item.id === "3" && mapOptions.ccsProjects
                    ? "primary"
                    : props.item.id === "4" && mapOptions.blocks
                    ? "primary"
                    : props.item.id === "5" && mapOptions.licences
                    ? "primary"
                    : props.item.id === "6" && mapOptions.windfarms
                    ? "primary"
                    : "light"
                }
                onClick={(event) => {
                  event.stopPropagation()
                  
                  const itemId = props.item.id as string;
                  const elementKey = buttonIdToElementMap[itemId as keyof typeof buttonIdToElementMap];
                  const currentValue = mapOptions[elementKey];
                  
                  // Check if user has required access for this item
                  if (shouldDisableItem || !blockUnauthorizedAction(props.item.id, 'toggle visibility')) {
                    showLicenseError('toggle visibility');
                    return;
                  }
                  
                  handleChangeValue({ element: elementKey, value: !currentValue, group: "serverSideOptions" })
                }}
              ></Button>
            </>
          ) : null}
        </div>
      ) : (
        <Component key={props.item.id} props={props} appliedFilterClassName={filterClassName} disabled={shouldDisableItem} />
      )}
      <div className="infoWindows">
      {showStyles && <StyleSettings centered={true}   handleCloseInfoWindow={() => setShowStyles(false)} pointData={pointData} closeStyles={() => setShowStyles(false)} />}
      </div>
      {openErrorPopup && <ErrorPopup handleClose={() => setOpenErrorPopup(false)}></ErrorPopup>}
      <LicenseErrorPopup
        isOpen={licenseError.isOpen}
        onClose={() => setLicenseError(prev => ({ ...prev, isOpen: false }))}
        datasetName={licenseError.datasetName}
        action={licenseError.action}
        reason={licenseError.reason}
      />
    </>
  )
}

export default React.memo(TreeItem)
